import { useEffect, useRef, useState } from "react";
import { labelDeclarations, styleFor } from "../../../shared/page-model/css.ts";
import type { Element, FrameElement, Screen } from "../../../shared/page-model/vocabulary.ts";
import { imageUrl, styleObject } from "./pageEditorLogic.ts";

/**
 * Draws a screen model live, with the same flex mapping render_html.py uses (shared/page-model/css.ts),
 * scaled to fit the chat's width. Every element carries its id; clicking selects it.
 */
export function Page({
	screen,
	selected,
	onSelect,
	sessionId,
	source,
}: {
	screen: Screen;
	selected: string | null;
	onSelect: (id: string) => void;
	sessionId: string;
	source: string;
}): React.ReactNode {
	const holder = useRef<HTMLDivElement>(null);
	const inner = useRef<HTMLDivElement>(null);
	const [scale, setScale] = useState(1);
	const [height, setHeight] = useState(0);

	// Fit the screen's fixed width into the holder, and reserve the scaled height so the layout
	// below does not sit under the transformed page.
	useEffect(() => {
		const holderEl = holder.current;
		const innerEl = inner.current;
		if (!holderEl || !innerEl) return;
		const measure = () => {
			const s = Math.min(1, holderEl.clientWidth / screen.width);
			setScale(s);
			setHeight(innerEl.offsetHeight * s);
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(holderEl);
		observer.observe(innerEl);
		return () => observer.disconnect();
	}, [screen]);

	return (
		<div className="page-holder" ref={holder} style={{ height: height || undefined }}>
			<div
				ref={inner}
				className="page-canvas"
				style={{ width: screen.width, transform: `scale(${scale})`, transformOrigin: "top left", fontFamily: `${screen.theme.font}, sans-serif` }}
			>
				<Node node={screen.root} parent={null} screen={screen} selected={selected} onSelect={onSelect} sessionId={sessionId} source={source} />
			</div>
		</div>
	);
}

function Node({
	node,
	parent,
	screen,
	selected,
	onSelect,
	sessionId,
	source,
}: {
	node: Element;
	parent: FrameElement | null;
	screen: Screen;
	selected: string | null;
	onSelect: (id: string) => void;
	sessionId: string;
	source: string;
}): React.ReactNode {
	const style = styleObject(styleFor(node, parent, screen.theme));
	const className = `page-el${selected === node.id ? " selected" : ""}`;
	const pick = (e: React.MouseEvent) => {
		e.stopPropagation();
		onSelect(node.id);
	};
	switch (node.kind) {
		case "frame":
			return (
				<div data-id={node.id} className={className} style={style} onClick={pick}>
					{(node.children ?? []).map((child) => (
						<Node key={child.id} node={child} parent={node} screen={screen} selected={selected} onSelect={onSelect} sessionId={sessionId} source={source} />
					))}
				</div>
			);
		case "text":
			return (
				<div data-id={node.id} className={className} style={style} onClick={pick}>
					{node.text}
				</div>
			);
		case "image":
			if (node.src === "placeholder") return <div data-id={node.id} className={className} style={style} onClick={pick} />;
			return <img data-id={node.id} className={className} style={style} alt="" src={imageUrl(sessionId, source, node.src)} onClick={pick} />;
		case "button": {
			// The label is not an element: a click on it selects the button.
			const label = styleObject(Object.fromEntries(labelDeclarations(node, screen.theme)));
			return (
				<div data-id={node.id} className={className} style={style} onClick={pick}>
					<span data-id={`${node.id}.label`} style={label}>
						{node.label}
					</span>
				</div>
			);
		}
	}
}
